import React, { useCallback, useMemo, useRef, useState } from "react";
import { FormattedMessage, injectIntl, IntlShape } from "react-intl";
import { SortableHandle } from "react-sortable-hoc";
import { Link, useHistory } from "react-router-dom";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faPencilAlt } from "@fortawesome/free-solid-svg-icons";
import { Table } from "@devexpress/dx-react-grid-bootstrap4";

import SimpleCrud from "../../shared/crud/simple-crud";
import { DefaultTableRowActionsProps, Delete } from "../../shared/actions/tableActions";
import { ImageManagerProvider } from "../../shared/widgets/ImageManager";
import displaySchema from "../schemas/display.json";
import getTableColumns from "../tableColumns";
import { messages } from "../messages";
import { SECTION } from "../../../lib/constants";
import { useDisplayApi } from "../hooks/useDisplayApi";
import { inferDisplayTypeAndEntityFilter } from "../utils/inferDisplayType";
import { reorderList } from "../utils/reorderList";

import { StyledSwapArrow } from "../styles";
import { DisplayEntity, DisplayListEntity, LocaleCode } from "../types";
import { useApiClient } from "../../../lib/http";
import { withAuthorizationVerifier } from "../../../components/authorization-verifier";

function DisplayCrud({ propertyTicker, intl }: { propertyTicker: string; intl: IntlShape }) {
  const httpClient = useApiClient();
  const history = useHistory();
  const [forceUpdate, setForceUpdate] = useState(0);

  const rowsRef = useRef<DisplayListEntity[]>([]);
  const positionOverridesRef = useRef<Record<string, number>>({});

  const { getAll, remove, reorder } = useDisplayApi(httpClient, propertyTicker);

  const customSchema = useMemo(() => displaySchema, []);
  const tableColumns = useMemo(() => getTableColumns(intl), [intl]);

  const listHeight = useMemo(() => window.innerHeight - window.innerHeight / 4, []);

  const handleGetAll = useCallback(async (): Promise<DisplayListEntity[]> => {
    const display = await getAll();
    const currentLang = (intl.locale || "es").slice(0, 2) as LocaleCode;

    let mapped: DisplayListEntity[] = display.map((d: DisplayEntity) => {
      const translatedLabel =
        d.translations?.find(t => t.locale === currentLang)?.label ??
        d.translations?.[0]?.label ??
        d.label ??
        "";

      const {
        contentType: inferred,
        entityFilter: inferredEntityFilter,
      } = inferDisplayTypeAndEntityFilter(d);

      return {
        ...d,
        id: d.id ?? "",
        propertyTicker: d.propertyTicker ?? propertyTicker,
        defaultOption: d.defaultOption ?? false,
        entityFilter: inferredEntityFilter,
        visible: `${d.startDate ?? ""} - ${d.endDate ?? ""}`,
        label: translatedLabel,
        tickerFilter: d.conditions?.find(c => c.type === "TICKER")?.value?.join(",") ?? "",
        contentType: inferred,
        position: d.position ?? 0,
      } as DisplayListEntity;
    });

    const ov = positionOverridesRef.current;
    if (ov && Object.keys(ov).length > 0) {
      mapped = mapped.map(it => (ov[it.id] != null ? { ...it, position: ov[it.id] } : it));
    }

    mapped.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    mapped = mapped.map((it, idx) => ({ ...it, position: idx }));

    rowsRef.current = mapped;
    return mapped;
  }, [getAll, intl.locale, propertyTicker]);

  const handleDelete = useCallback(
    async (id: string) => {
      await remove(id);
      setForceUpdate(prev => prev + 1);
    },
    [remove]
  );

  const handleReOrder = useCallback(
    async (id: string, after?: string) => {
      const current = reorderList(rowsRef.current, id, after);

      const ov: Record<string, number> = {};
      current.forEach((item, idx) => {
        ov[item.id] = idx;
      });
      positionOverridesRef.current = ov;
      rowsRef.current = current;

      setForceUpdate(prev => prev + 1);

      await reorder(id, after ?? undefined);
    },
    [reorder]
  );

  const handleEdit = useCallback(
    (id: string) => {
      history.push(`/${propertyTicker}/display/${id}`);
    },
    [history, propertyTicker]
  );

  const handleDuplicate = useCallback(
    (id: string) => {
      history.push(`/${propertyTicker}/display-copy/${id}`);
    },
    [history, propertyTicker]
  );

  const TableRowActions = useCallback(
    ({ row, rest }: DefaultTableRowActionsProps<DisplayListEntity>) => {
      const DragButton = SortableHandle(() => (
        <OverlayTrigger
          delay={{ show: 250, hide: 0 }}
          overlay={props => (
            <Tooltip id={`drag-tooltip-${row.id}`} {...props}>
              <FormattedMessage {...messages.drag} />
            </Tooltip>
          )}>
          <Button size="sm" className="mr-2" variant="light" style={{ cursor: "move" }}>
            <StyledSwapArrow />
          </Button>
        </OverlayTrigger>
      ));

      return (
        <Table.Cell {...rest}>
          <DragButton />

          {/* Duplicate */}
          <OverlayTrigger
            delay={{ show: 250, hide: 0 }}
            overlay={props => (
              <Tooltip id={`duplicate-tooltip-${row.id}`} {...props}>
                <FormattedMessage {...messages.duplicate} />
              </Tooltip>
            )}>
            <Button
              size="sm"
              className="mr-2"
              variant="light"
              onClick={() => handleDuplicate(row?.id)}>
              <FontAwesomeIcon icon={faCopy} />
            </Button>
          </OverlayTrigger>

          {/* Edit */}
          <OverlayTrigger
            delay={{ show: 250, hide: 0 }}
            overlay={props => (
              <Tooltip id={`edit-tooltip-${row.id}`} {...props}>
                <FormattedMessage {...messages.edit} />
              </Tooltip>
            )}>
            <Button size="sm" className="mr-2" variant="light" onClick={() => handleEdit(row?.id)}>
              <FontAwesomeIcon icon={faPencilAlt} />
            </Button>
          </OverlayTrigger>

          {/* Remove */}
          <Delete
            rest={rest}
            onDelete={handleDelete}
            row={row}
            onReloadList={() => setForceUpdate(prev => prev + 1)}
            useOverlay={true}
          />
        </Table.Cell>
      );
    },
    [handleDelete, handleDuplicate, handleEdit]
  );

  const actions = () => (
    <Link className="btn btn-primary" to="display/new">
      <FormattedMessage {...messages.create} />
    </Link>
  );

  return (
    <ImageManagerProvider>
      <SimpleCrud<DisplayListEntity>
        managerName={"display"}
        fixedActions={true}
        actions={actions}
        listHeight={listHeight}
        formConfiguration={{}}
        schema={customSchema}
        tableColumns={tableColumns}
        section={SECTION.DISPLAY}
        onGetAll={handleGetAll}
        onDelete={handleDelete}
        onReorder={handleReOrder}
        messages={messages}
        rowActions={props => <TableRowActions {...props} />}
        forceLoad={forceUpdate}
        hideExportButton={true}
      />
    </ImageManagerProvider>
  );
}

export const DisplayList = withAuthorizationVerifier(injectIntl(DisplayCrud));
